/**
 * Re-export Drizzle enums for type consistency across the codebase.
 * These are inferred from the pgEnum definitions in the database schema.
 */
import type {
  accessSourceType,
  accessStatus,
  accessTargetType,
  tagTargetType,
  broadcastStatus,
  documentStatus,
  fileAccess,
  fileStatus,
  homeworkStatus,
  membershipStatus,
  userBotLinkStatus,
  moduleItemType,
  notificationChannel,
  notificationType,
  emailTemplateKey,
  orderTargetType,
  paymentProvider,
  paymentStatus,
  repeatType,
  reviewTargetType,
  subscriptionStatus,
  translatableEntityType,
  miniBannerTargetType,
  promoAnnouncementEventType,
  triggerEvent,
  // New enums
  courseType,
  videoProcessingStatus,
  familyMemberStatus,
  limitAction,
  // Recommendations
  embeddableEntityType,
  // Chat enums
  chatRoomType,
  chatParticipantRole,
  chatParticipantStatus,
  messageDeliveryStatus,
  // Games enums
  gameType,
  lobbyStatus,
  lobbyPlayerStatus,
  // Pricing & Bonuses enums
  bonusType,
  bonusDurationMode,
  userBonusStatus,
  userBonusActionType,
  // E2EE enums
  devicePlatform,
  deviceStatus,
  keyBackupStatus,
  chatRoomKind,
  resetCodeChannel,
  // OAuth providers
  oauthProvider,
  // Library
  bookFormat,
  bookProcessingStatus,
  // Livestream
  livestreamSource,
  livestreamStatus,
  livestreamVisibility,
  translationSegmentPolicy,
  translationVoiceMode,
  translationViewerMode,
  translationOriginalMix,
  translationStartPolicy,
  translationSessionStatus,
  translationVoiceProfileKind,
  translationVoiceProvider,
  // Presence (online tracking)
  presenceSource,
  // Storage
  storageProvider,
  // Leads CRM
  leadTagSource,
  // Video Calls
  callRoomType,
  callRoomStatus,
  callParticipantRole,
  callInvitationStatus,
  // Breakout Rooms
  breakoutSessionStatus,
  breakoutSessionMode,
  // Promo Codes
  promoDiscountType,
  promoTargetType,
  // User Invitations
  invitationStatus,
  invitationChannel,
  // Fiscal Receipts (54-ФЗ)
  fiscalProvider,
  fiscalOperation,
  fiscalReceiptStatus,
  // MCP
  mcpTokenKind,
} from '@masters-club/db';

// Extract enum value types from Drizzle pgEnum
type EnumValues<T extends { enumValues: readonly string[] }> = T['enumValues'][number];

// Subscription
export type SubscriptionStatus = EnumValues<typeof subscriptionStatus>;
export const SubscriptionStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;

// Payments
export type PaymentProvider = EnumValues<typeof paymentProvider>;
export const PaymentProvider = {
  MANUAL: 'MANUAL',
  CLICK: 'CLICK',
  PAYME: 'PAYME',
  STRIPE: 'STRIPE',
  MULTICARD: 'MULTICARD',
  CLOUDPAYMENTS: 'CLOUDPAYMENTS',
} as const;

export type PaymentStatus = EnumValues<typeof paymentStatus>;
export const PaymentStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  REJECTED: 'REJECTED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
} as const;

// Modules & Content
export type ModuleItemType = EnumValues<typeof moduleItemType>;
export const ModuleItemType = {
  VIDEO: 'VIDEO',
  ARTICLE: 'ARTICLE',
  HOMEWORK: 'HOMEWORK',
  BOOK: 'BOOK',
  AUDIO: 'AUDIO',
} as const;

export type HomeworkStatus = EnumValues<typeof homeworkStatus>;
export const HomeworkStatus = {
  NOT_STARTED: 'NOT_STARTED',
  SUBMITTED: 'SUBMITTED',
  REVIEWED: 'REVIEWED',
} as const;

// Daily Planner
export type RepeatType = EnumValues<typeof repeatType>;
export const RepeatType = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  CUSTOM: 'CUSTOM',
} as const;

// Notifications
export type NotificationType = EnumValues<typeof notificationType>;
export const NotificationType = {
  // User-facing
  SUBSCRIPTION_EXPIRING: 'SUBSCRIPTION_EXPIRING',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  COURSE_AVAILABLE: 'COURSE_AVAILABLE',
  HOMEWORK_REMINDER: 'HOMEWORK_REMINDER',
  BROADCAST: 'BROADCAST',
  SYSTEM: 'SYSTEM',
  MARKETING: 'MARKETING',
  FAMILY_SLOTS_INCREASED: 'FAMILY_SLOTS_INCREASED',
  FAMILY_SLOTS_DECREASED: 'FAMILY_SLOTS_DECREASED',
  FAMILY_MEMBER_ADDED: 'FAMILY_MEMBER_ADDED',
  FAMILY_MEMBER_REMOVED: 'FAMILY_MEMBER_REMOVED',
  // Admin-facing — см. AdminNotifier в apps/backend/src/domains/notifications/admin-notifier.ts.
  ADMIN_PAYMENT_SUCCESS: 'ADMIN_PAYMENT_SUCCESS',
  ADMIN_PAYMENT_FAILED: 'ADMIN_PAYMENT_FAILED',
  ADMIN_PAYMENT_REFUND: 'ADMIN_PAYMENT_REFUND',
  ADMIN_FISCAL_ERROR: 'ADMIN_FISCAL_ERROR',
  ADMIN_ORDER_NEW: 'ADMIN_ORDER_NEW',
  ADMIN_ORDER_CANCELLED: 'ADMIN_ORDER_CANCELLED',
  ADMIN_SUPPORT_TICKET_NEW: 'ADMIN_SUPPORT_TICKET_NEW',
  ADMIN_SUPPORT_TICKET_REPLY: 'ADMIN_SUPPORT_TICKET_REPLY',
  ADMIN_LIVESTREAM_STARTED: 'ADMIN_LIVESTREAM_STARTED',
  ADMIN_LIVESTREAM_STREAM_LOST: 'ADMIN_LIVESTREAM_STREAM_LOST',
  ADMIN_LIVESTREAM_OBS_ERROR: 'ADMIN_LIVESTREAM_OBS_ERROR',
  ADMIN_TELEGRAM_SESSION_EXPIRED: 'ADMIN_TELEGRAM_SESSION_EXPIRED',
  ADMIN_TELEGRAM_FLOOD_WAIT: 'ADMIN_TELEGRAM_FLOOD_WAIT',
  ADMIN_USER_DUPLICATE: 'ADMIN_USER_DUPLICATE',
  ADMIN_ENTITY_CREATED: 'ADMIN_ENTITY_CREATED',
  ADMIN_ENTITY_UPDATED: 'ADMIN_ENTITY_UPDATED',
  ADMIN_ENTITY_DELETED: 'ADMIN_ENTITY_DELETED',
  ADMIN_ERROR_CRITICAL: 'ADMIN_ERROR_CRITICAL',
  ADMIN_PROMO_LIMIT: 'ADMIN_PROMO_LIMIT',
  ADMIN_PROMO_EXPIRED: 'ADMIN_PROMO_EXPIRED',
  ADMIN_DISCOUNT_BUDGET_EXCEEDED: 'ADMIN_DISCOUNT_BUDGET_EXCEEDED',
  ADMIN_RECURRENT_FAILED: 'ADMIN_RECURRENT_FAILED',
} as const;

export type NotificationChannel = EnumValues<typeof notificationChannel>;
export const NotificationChannel = {
  APP: 'APP',
  EMAIL: 'EMAIL',
  TELEGRAM: 'TELEGRAM',
} as const;

// Email Templates — ключ шаблона письма.
// Соответствует pgEnum `EmailTemplateKey` в packages/db/src/drizzle/schema/enums.ts
// и шаблонам в apps/backend/src/shared/email-templates/*.tsx.
export type EmailTemplateKey = EnumValues<typeof emailTemplateKey>;
export const EmailTemplateKey = {
  OTP: 'OTP',
  ADMIN_INVITE: 'ADMIN_INVITE',
  ADMIN_RESET: 'ADMIN_RESET',
  NOTIFICATION: 'NOTIFICATION',
} as const;

// Triggers
export type TriggerEvent = EnumValues<typeof triggerEvent>;
export const TriggerEvent = {
  SUBSCRIPTION_PURCHASED: 'SUBSCRIPTION_PURCHASED',
  SUBSCRIPTION_STARTED: 'SUBSCRIPTION_STARTED',
  SUBSCRIPTION_EXPIRING: 'SUBSCRIPTION_EXPIRING',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  SUBSCRIPTION_EXPIRATION: 'SUBSCRIPTION_EXPIRATION',
  ORDER_CREATED: 'ORDER_CREATED',
  ORDER_PAID: 'ORDER_PAID',
  ORDER_CANCELLED: 'ORDER_CANCELLED',
  COURSE_COMPLETED: 'COURSE_COMPLETED',
  HOMEWORK_SUBMITTED: 'HOMEWORK_SUBMITTED',
  USER_REGISTERED: 'USER_REGISTERED',
  NEW_COURSE_AVAILABLE: 'NEW_COURSE_AVAILABLE',
  CHANNEL_PASS_LIMIT_REACHED: 'CHANNEL_PASS_LIMIT_REACHED',
  CHANNEL_PASS_EXPIRED: 'CHANNEL_PASS_EXPIRED',
  COURSE_ACCESS_EXPIRED: 'COURSE_ACCESS_EXPIRED',
  COURSE_DRIP_RELEASED: 'COURSE_DRIP_RELEASED',
  FAMILY_MEMBER_ADDED: 'FAMILY_MEMBER_ADDED',
  FAMILY_MEMBER_REMOVED: 'FAMILY_MEMBER_REMOVED',
  FAMILY_SLOTS_CHANGED: 'FAMILY_SLOTS_CHANGED',
  LIVESTREAM_STARTED: 'LIVESTREAM_STARTED',
  LIVESTREAM_SCHEDULED_REMINDER: 'LIVESTREAM_SCHEDULED_REMINDER',
  LIVESTREAM_CANCELLED: 'LIVESTREAM_CANCELLED',
  LIVESTREAM_ENDING_SOON: 'LIVESTREAM_ENDING_SOON',
  // Низкоуровневое событие выдачи доступа (выпускается из centrall grantAccess()).
  // В отличие от SUBSCRIPTION_STARTED, это per-grant событие — один grantAccess
  // создаёт ровно одно событие, включая ручные/бонусные/реферальные источники.
  ACCESS_GRANTED: 'ACCESS_GRANTED',
  // Ответ на комментарий пользователя к уроку-статье. Получатель — автор
  // родительского коммента. Эмитится из lesson-comments.service.ts.reply().
  COMMENT_REPLY: 'COMMENT_REPLY',
} as const;

// Commerce
export type OrderTargetType = EnumValues<typeof orderTargetType>;
export const OrderTargetType = {
  COURSE: 'COURSE',
  CHANNEL_PASS: 'CHANNEL_PASS',
  SUBSCRIPTION_PLAN: 'SUBSCRIPTION_PLAN',
  EVENT: 'EVENT',
  BONUS: 'BONUS',
  BOOK: 'BOOK',
} as const;

// Fiscal Receipts (54-ФЗ) — multi-provider: CloudPayments + Бизнес.ру.
// Зеркало PG-enum'ов из packages/db/src/drizzle/schema/fiscal.ts.
export type FiscalProvider = EnumValues<typeof fiscalProvider>;
export const FiscalProvider = {
  CLOUDPAYMENTS: 'CLOUDPAYMENTS',
  BUSINESS_RU: 'BUSINESS_RU',
} as const;

export type FiscalOperation = EnumValues<typeof fiscalOperation>;
export const FiscalOperation = {
  SELL: 'SELL',
  REFUND: 'REFUND',
  CORRECTION_SELL: 'CORRECTION_SELL',
  CORRECTION_REFUND: 'CORRECTION_REFUND',
} as const;

export type FiscalReceiptStatus = EnumValues<typeof fiscalReceiptStatus>;
export const FiscalReceiptStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;

export type ReviewTargetType = EnumValues<typeof reviewTargetType>;
export const ReviewTargetType = {
  COURSE: 'COURSE',
  CHANNEL_PASS: 'CHANNEL_PASS',
  SUBSCRIPTION_PLAN: 'SUBSCRIPTION_PLAN',
  EVENT: 'EVENT',
} as const;

// Access & Entitlements
export type AccessTargetType = EnumValues<typeof accessTargetType>;
export const AccessTargetType = {
  COURSE: 'COURSE',
  MODULE: 'MODULE',
  MODULE_ITEM: 'MODULE_ITEM',
  CHANNEL: 'CHANNEL',
  EVENT: 'EVENT',
  CONTENT: 'CONTENT',
  BOOK: 'BOOK',
  LIVESTREAM: 'LIVESTREAM',
} as const;

// Tag Target — единый enum типов сущностей для TagAssignment.
// USER — атрибут пользователя, остальные — видимость контента.
export type TagTargetType = EnumValues<typeof tagTargetType>;
export const TagTargetType = {
  USER: 'USER',
  BOOK: 'BOOK',
  COURSE: 'COURSE',
  MODULE: 'MODULE',
  MODULE_ITEM: 'MODULE_ITEM',
  LIVESTREAM: 'LIVESTREAM',
  TELEGRAM_CHANNEL: 'TELEGRAM_CHANNEL',
  CHANNEL_PASS: 'CHANNEL_PASS',
  SUBSCRIPTION_PLAN: 'SUBSCRIPTION_PLAN',
  PRICING_TIER: 'PRICING_TIER',
  BANNER: 'BANNER',
  MINI_BANNER: 'MINI_BANNER',
  PROMO_ANNOUNCEMENT: 'PROMO_ANNOUNCEMENT',
} as const;

// Подмножество TagTargetType для контент-сущностей (используется в визибилити).
export type ContentTagTargetType = Exclude<TagTargetType, 'USER'>;

export type AccessSourceType = EnumValues<typeof accessSourceType>;
export const AccessSourceType = {
  SUBSCRIPTION: 'SUBSCRIPTION',
  MANUAL: 'MANUAL',
  PROMOTION: 'PROMOTION',
  GIFT: 'GIFT',
  ORDER: 'ORDER',
} as const;

export type AccessStatus = EnumValues<typeof accessStatus>;
export const AccessStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
} as const;

// Translations
export type TranslatableEntityType = EnumValues<typeof translatableEntityType>;
export const TranslatableEntityType = {
  COURSE: 'COURSE',
  MODULE: 'MODULE',
  MODULE_ITEM: 'MODULE_ITEM',
  ARTICLE: 'ARTICLE',
  BANNER: 'BANNER',
  CATEGORY: 'CATEGORY',
  TAG: 'TAG',
  TEACHER: 'TEACHER',
  GOAL: 'GOAL',
  REMINDER: 'REMINDER',
  EVENT: 'EVENT',
  CHANNEL_PASS: 'CHANNEL_PASS',
  SUBSCRIPTION_PLAN: 'SUBSCRIPTION_PLAN',
  BROADCAST: 'BROADCAST',
  PRICING_TIER: 'PRICING_TIER',
  BONUS_TEMPLATE: 'BONUS_TEMPLATE',
  PROMOTION: 'PROMOTION',
  CLUB_SETTINGS: 'CLUB_SETTINGS',
  LEGAL_PAGE: 'LEGAL_PAGE',
  MINI_BANNER: 'MINI_BANNER',
  LIVESTREAM: 'LIVESTREAM',
} as const;

// Mini banner — target scope
export type MiniBannerTargetType = EnumValues<typeof miniBannerTargetType>;
export const MiniBannerTargetType = {
  COURSE: 'COURSE',
  MODULE: 'MODULE',
  MODULE_ITEM: 'MODULE_ITEM',
  LIVESTREAM: 'LIVESTREAM',
} as const;

// Promo announcement — тип события аналитики (fullscreen-промо при входе)
export type PromoAnnouncementEventType = EnumValues<typeof promoAnnouncementEventType>;
export const PromoAnnouncementEventType = {
  IMPRESSION: 'IMPRESSION',
  CLICK: 'CLICK',
  CLOSE: 'CLOSE',
} as const;

// Broadcasts
export type BroadcastStatus = EnumValues<typeof broadcastStatus>;
export const BroadcastStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  SENDING: 'SENDING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const;

// Channel Memberships
export type MembershipStatus = EnumValues<typeof membershipStatus>;
export const MembershipStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  LEFT: 'LEFT',
  KICKED: 'KICKED',
  BOT_BLOCKED: 'BOT_BLOCKED',
  EXPIRED: 'EXPIRED',
  JOIN_REQUEST_SENT: 'JOIN_REQUEST_SENT',
} as const;

// User ↔ Telegram Bot link
export type UserBotLinkStatus = EnumValues<typeof userBotLinkStatus>;
export const UserBotLinkStatus = {
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
} as const;

// Files
export type FileAccess = EnumValues<typeof fileAccess>;
export const FileAccess = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
} as const;

export type FileStatus = EnumValues<typeof fileStatus>;
export const FileStatus = {
  UPLOADED: 'UPLOADED',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  ERROR: 'ERROR',
} as const;

// StorageProvider — источник правды о том, где лежит файл.
// LOCAL — файловая система backend-хоста (UPLOAD_DIR / STORAGE_DIR).
// B2    — Backblaze B2 объектное хранилище (S3-совместимый API).
// `File.bucket` уточняет конкретный bucket внутри провайдера.
export type StorageProvider = EnumValues<typeof storageProvider>;
export const StorageProvider = {
  LOCAL: 'LOCAL',
  B2: 'B2',
} as const;

// Knowledge Base
export type DocumentStatus = EnumValues<typeof documentStatus>;
export const DocumentStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

// Courses
export type CourseType = EnumValues<typeof courseType>;
export const CourseType = {
  ONLINE: 'ONLINE',
  MEETING: 'MEETING',
  OFFLINE: 'OFFLINE',
  WEBINAR: 'WEBINAR',
} as const;

// Video Processing
export type VideoProcessingStatus = EnumValues<typeof videoProcessingStatus>;
export const VideoProcessingStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  ERROR: 'ERROR',
} as const;

// Family Subscriptions
export type FamilyMemberStatus = EnumValues<typeof familyMemberStatus>;
export const FamilyMemberStatus = {
  ACTIVE: 'ACTIVE',
  REMOVED: 'REMOVED',
  PENDING: 'PENDING',
} as const;

// Channel Pass Limits
export type LimitAction = EnumValues<typeof limitAction>;
export const LimitAction = {
  HIDE_FROM_STORE: 'HIDE_FROM_STORE',
  DEACTIVATE: 'DEACTIVATE',
  TRIGGER: 'TRIGGER',
} as const;

// Recommendations
export type EmbeddableEntityType = EnumValues<typeof embeddableEntityType>;
export const EmbeddableEntityType = {
  COURSE: 'COURSE',
  ARTICLE: 'ARTICLE',
  ANNOUNCEMENT: 'ANNOUNCEMENT',
  POST: 'POST',
} as const;

// ============================================
// Library / Books
// ============================================

export type BookFormat = EnumValues<typeof bookFormat>;
export const BookFormat = {
  EPUB: 'EPUB',
  PDF: 'PDF',
  FB2: 'FB2',
  AUDIO: 'AUDIO',
} as const;

export type BookProcessingStatus = EnumValues<typeof bookProcessingStatus>;
export const BookProcessingStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  ERROR: 'ERROR',
} as const;

// ============================================
// Presence / Online Tracking
// ============================================

/**
 * Источник онлайн-события — фиксирует, откуда пришёл heartbeat.
 * Используется в таблице `UserPresenceLog` для построения Gantt-истории
 * онлайна по источникам (WEB — платформа, BOT — Telegram, MINI_APP — TWA).
 */
export type PresenceSource = EnumValues<typeof presenceSource>;
export const PresenceSource = {
  WEB: 'WEB',
  BOT: 'BOT',
  MINI_APP: 'MINI_APP',
} as const;

/**
 * Направление текста для языков (не в DB schema)
 */
export const TextDirection = {
  LTR: 'ltr',
  RTL: 'rtl',
} as const;

export type TextDirection = (typeof TextDirection)[keyof typeof TextDirection];

// ============================================
// Chat Enums
// ============================================

export type ChatRoomType = EnumValues<typeof chatRoomType>;
export const ChatRoomType = {
  DIRECT: 'DIRECT',
  SUPPORT: 'SUPPORT',
  GAME_LOBBY: 'GAME_LOBBY',
  GROUP: 'GROUP',
} as const;

export type ChatParticipantRole = EnumValues<typeof chatParticipantRole>;
export const ChatParticipantRole = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
} as const;

export type ChatParticipantStatus = EnumValues<typeof chatParticipantStatus>;
export const ChatParticipantStatus = {
  ACTIVE: 'ACTIVE',
  LEFT: 'LEFT',
  KICKED: 'KICKED',
  BLOCKED: 'BLOCKED',
} as const;

export type MessageDeliveryStatus = EnumValues<typeof messageDeliveryStatus>;
export const MessageDeliveryStatus = {
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  READ: 'READ',
} as const;

/**
 * Actor type used across chat WS events and UI to distinguish
 * regular users from admin users in a chat room.
 */
export type UserActorType = 'user' | 'admin';
export const UserActorType = {
  USER: 'user',
  ADMIN: 'admin',
} as const;

// ============================================
// Games Enums
// ============================================

export type GameType = EnumValues<typeof gameType>;
export const GameType = {
  CARD_COLOR: 'CARD_COLOR',
  GUESS_NUMBER: 'GUESS_NUMBER',
} as const;

export type LobbyStatus = EnumValues<typeof lobbyStatus>;
export const LobbyStatus = {
  WAITING: 'WAITING',
  STARTING: 'STARTING',
  IN_PROGRESS: 'IN_PROGRESS',
  FINISHED: 'FINISHED',
  CANCELLED: 'CANCELLED',
} as const;

export type LobbyPlayerStatus = EnumValues<typeof lobbyPlayerStatus>;
export const LobbyPlayerStatus = {
  WAITING: 'WAITING',
  READY: 'READY',
  PLAYING: 'PLAYING',
  LEFT: 'LEFT',
  KICKED: 'KICKED',
} as const;

// ============================================
// Pricing & Bonuses Enums
// ============================================

export type BonusType = EnumValues<typeof bonusType>;
export const BonusType = {
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN',
  ARRAY: 'ARRAY',
  STRING: 'STRING',
} as const;

export type BonusDurationMode = EnumValues<typeof bonusDurationMode>;
export const BonusDurationMode = {
  RELATIVE: 'RELATIVE',
  ABSOLUTE: 'ABSOLUTE',
  INHERIT: 'INHERIT',
} as const;

export type UserBonusStatus = EnumValues<typeof userBonusStatus>;
export const UserBonusStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
} as const;

export type UserBonusActionType = EnumValues<typeof userBonusActionType>;
export const UserBonusActionType = {
  INCREMENT: 'INCREMENT',
  DECREMENT: 'DECREMENT',
  SET: 'SET',
  PUSH: 'PUSH',
  REMOVE: 'REMOVE',
  REVOKE: 'REVOKE',
  EXPIRE: 'EXPIRE',
} as const;

// ============================================
// E2EE — End-to-end encryption
// ============================================

// Платформа устройства (привязка к Olm device).
// PLATFORM — apps/platform (web/Next.js), MINI_APP — Telegram Mini App,
// BOT — bot-side device, DESKTOP — отдельные desktop-клиенты.
export type DevicePlatform = EnumValues<typeof devicePlatform>;
export const DevicePlatform = {
  PLATFORM: 'PLATFORM',
  MINI_APP: 'MINI_APP',
  BOT: 'BOT',
  DESKTOP: 'DESKTOP',
} as const;

// Статус устройства. ACTIVE — может слать/принимать.
// REVOKED — юзер логически удалил девайс; ключи не выдаются.
export type DeviceStatus = EnumValues<typeof deviceStatus>;
export const DeviceStatus = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
} as const;

// Статус key-backup-а. ACTIVE — текущая используемая версия бэкапа.
// TRASHED — старая версия, оставлена для возможного recovery.
export type KeyBackupStatus = EnumValues<typeof keyBackupStatus>;
export const KeyBackupStatus = {
  ACTIVE: 'ACTIVE',
  TRASHED: 'TRASHED',
} as const;

// Канал доставки reset-кода при сбросе PIN (EMAIL / TELEGRAM).
// Соответствует pgEnum `ResetCodeChannel` в packages/db/src/drizzle/schema/enums.ts.
export type ResetCodeChannel = EnumValues<typeof resetCodeChannel>;
export const ResetCodeChannel = {
  EMAIL: 'EMAIL',
  TELEGRAM: 'TELEGRAM',
} as const;

// Тип чата по политике приватности. Отдельный от исторического ChatRoomType.
// USER_E2EE — сквозное шифрование между юзерами (Olm/Megolm),
// SERVICE — служебные сообщения от бота (без E2EE),
// ADMIN — переписка с админом/поддержкой (без E2EE, админ читает plaintext),
// DIRECT — 1:1 чат без шифрования (legacy/non-E2EE),
// GROUP — групповой чат без шифрования.
// LIVESTREAM — чат прямой трансляции.
export type ChatRoomKind = EnumValues<typeof chatRoomKind>;
export const ChatRoomKind = {
  USER_E2EE: 'USER_E2EE',
  SERVICE: 'SERVICE',
  ADMIN: 'ADMIN',
  DIRECT: 'DIRECT',
  GROUP: 'GROUP',
  LIVESTREAM: 'LIVESTREAM',
} as const;

// ============================================
// User Block Reason
// ============================================
// Mirrors the `userBlockReason` pgEnum in packages/db/src/drizzle/schema/enums.ts.
// Kept standalone (not derived via EnumValues) to avoid a hard coupling while the
// DB enum lands in parallel; values MUST stay in sync with the pgEnum definition.
export const UserBlockReason = {
  SPAM: 'SPAM',
  ABUSE: 'ABUSE',
  FRAUD: 'FRAUD',
  PAYMENT_CHARGEBACK: 'PAYMENT_CHARGEBACK',
  TOS_VIOLATION: 'TOS_VIOLATION',
  OTHER: 'OTHER',
} as const;

export type UserBlockReason = (typeof UserBlockReason)[keyof typeof UserBlockReason];

// ============================================
// File Entity Type
// ============================================
// Используется для связи `File` с произвольной сущностью через таблицы
// вроде `FileUsage` и `ModuleItemAttachment`. НЕ pgEnum — это полиморфный
// тип, хранящийся как text в БД.
//
// Добавляй сюда новый кейс, когда добавляешь новое место использования
// файлов (курсы, уроки, учителя, аватары и т.п.).
export const FileEntityType = {
  MODULE_ITEM: 'MODULE_ITEM',
  COURSE: 'COURSE',
  TEACHER: 'TEACHER',
  USER: 'USER',
  HOMEWORK: 'HOMEWORK',
  BROADCAST: 'BROADCAST',
} as const;

export type FileEntityType = (typeof FileEntityType)[keyof typeof FileEntityType];

// ============================================
// OAuth Providers
// ============================================
// Внешние провайдеры авторизации для таблицы OAuthAccount.
// Соответствует pgEnum `OAuthProvider` в packages/db/src/drizzle/schema/enums.ts.

export type OAuthProvider = EnumValues<typeof oauthProvider>;
export const OAuthProvider = {
  GOOGLE: 'GOOGLE',
  APPLE: 'APPLE',
} as const;

// ============================================
// API Key Types
// ============================================
// Реестр известных типов записей в таблице ApiKey (хранится текстом, не enum в БД).
// Используется UI админки и backend сервисами для типобезопасной работы с конфигами.
//
// Каждый ApiKeyType имеет свою форму поля `value` (jsonb). Соответствие типов
// см. в `packages/types/src/api-keys.ts`.

export const ApiKeyType = {
  AMOCRM: 'AMOCRM',
  TELEGRAM_BOT: 'TELEGRAM_BOT',
  TELEGRAM_CLIENT: 'TELEGRAM_CLIENT',
  CLICK: 'CLICK',
  PAYME: 'PAYME',
  EMAIL_SMTP: 'EMAIL_SMTP',
  RAHMAT: 'RAHMAT',
  /** Отдельный мерчант Rahmat/Multicard для оплаты доступа к каналам. */
  RAHMAT_CHANNELS: 'RAHMAT_CHANNELS',
  CLOUDPAYMENTS: 'CLOUDPAYMENTS',
  OPENROUTER: 'OPENROUTER',
  MISTRAL: 'MISTRAL',
  ELEVENLABS: 'ELEVENLABS',
  STRIPE: 'STRIPE',
  GOOGLE_OAUTH: 'GOOGLE_OAUTH',
  OTHER: 'OTHER',
} as const;

export type ApiKeyType = (typeof ApiKeyType)[keyof typeof ApiKeyType];

// ============================================
// Livestream
// ============================================
// Соответствует pgEnum'ам `LivestreamStatus`, `LivestreamVisibility`,
// `LivestreamSource` в packages/db/src/drizzle/schema/enums.ts.

export type LivestreamStatus = EnumValues<typeof livestreamStatus>;
export const LivestreamStatus = {
  SCHEDULED: 'SCHEDULED',
  LIVE: 'LIVE',
  RECONNECTING: 'RECONNECTING',
  ENDED: 'ENDED',
  CANCELLED: 'CANCELLED',
} as const;

export type LivestreamVisibility = EnumValues<typeof livestreamVisibility>;
export const LivestreamVisibility = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
} as const;

export type LivestreamSource = EnumValues<typeof livestreamSource>;
export const LivestreamSource = {
  OBS: 'OBS',
  ZOOM: 'ZOOM',
} as const;

// ============================================
// Livestream real-time translation
// ============================================
// Соответствует pgEnum'ам `TranslationSegmentPolicy`, `TranslationVoiceMode`,
// `TranslationViewerMode`, `TranslationOriginalMix`, `TranslationStartPolicy`,
// `TranslationSessionStatus`, `TranslationVoiceProfileKind`,
// `TranslationVoiceProvider` в packages/db/src/drizzle/schema/enums.ts.
// Канон: docs/livestream-translation-architecture.md §5.2, §7.

// Политика нарезки речи на сегменты перед MT+TTS. Определяет, сколько ждать
// конца фразы: это structural floor задержки, а не «лишние накладные».
export type TranslationSegmentPolicy = EnumValues<typeof translationSegmentPolicy>;
export const TranslationSegmentPolicy = {
  BALANCED: 'BALANCED',
  LOW_LATENCY: 'LOW_LATENCY',
  HIGH_QUALITY: 'HIGH_QUALITY',
} as const;

// Как выбирается голос озвучки.
// SINGLE — один голос на весь эфир (дефолт v1, админ выбирает в табе).
// PER_SPEAKER — diarization label → slot → свой clone (только после gate).
// NEUTRAL — нейтральный пресет без клонов.
export type TranslationVoiceMode = EnumValues<typeof translationVoiceMode>;
export const TranslationVoiceMode = {
  SINGLE: 'SINGLE',
  PER_SPEAKER: 'PER_SPEAKER',
  NEUTRAL: 'NEUTRAL',
} as const;

// Режим восприятия зрителя.
export type TranslationViewerMode = EnumValues<typeof translationViewerMode>;
export const TranslationViewerMode = {
  ORIGINAL: 'ORIGINAL',
  CAPTIONS: 'CAPTIONS',
  DUB: 'DUB',
  DUB_PLUS_CAPTIONS: 'DUB_PLUS_CAPTIONS',
  MIX: 'MIX',
} as const;

// Что делать с оригинальной дорожкой под озвучкой.
// MUTE — заглушить (паттерн use-audio-track, baseline).
// MIX — обе дорожки с независимыми громкостями (продуктовая цель).
// DUCK_DB — авто-приглушение через Web Audio (позже).
export type TranslationOriginalMix = EnumValues<typeof translationOriginalMix>;
export const TranslationOriginalMix = {
  MUTE: 'MUTE',
  MIX: 'MIX',
  DUCK_DB: 'DUCK_DB',
} as const;

// Когда стартует сессия перевода.
export type TranslationStartPolicy = EnumValues<typeof translationStartPolicy>;
export const TranslationStartPolicy = {
  ON_LIVE: 'ON_LIVE',
  MANUAL: 'MANUAL',
} as const;

// Жизненный цикл сессии перевода (не путать со статусом самого эфира).
export type TranslationSessionStatus = EnumValues<typeof translationSessionStatus>;
export const TranslationSessionStatus = {
  STARTING: 'STARTING',
  LIVE: 'LIVE',
  DEGRADED: 'DEGRADED',
  STOPPING: 'STOPPING',
  STOPPED: 'STOPPED',
  FAILED: 'FAILED',
} as const;

// Происхождение голосового профиля в библиотеке тенанта.
export type TranslationVoiceProfileKind = EnumValues<typeof translationVoiceProfileKind>;
export const TranslationVoiceProfileKind = {
  CLONE: 'CLONE',
  PRESET: 'PRESET',
  NEUTRAL: 'NEUTRAL',
} as const;

// Провайдер синтеза для профиля голоса.
export type TranslationVoiceProvider = EnumValues<typeof translationVoiceProvider>;
export const TranslationVoiceProvider = {
  ELEVENLABS: 'ELEVENLABS',
} as const;

// ============================================
// UserTag Source
// ============================================
// Источник присвоения тега пользователю. Хранится как text в БД (не pgEnum),
// что позволяет расширять без миграций. Используется в таблице UserTag.

export const UserTagSource = {
  MANUAL: 'manual',
  UTM_AUTO: 'utm_auto',
  COUNTRY_AUTO: 'country_auto',
  SYSTEM: 'system',
} as const;

export type UserTagSource = (typeof UserTagSource)[keyof typeof UserTagSource];

// ============================================
// Leads CRM
// ============================================
// Источник присвоения тега лиду.
// Соответствует pgEnum `LeadTagSource` в packages/db/src/drizzle/schema/enums.ts.

export type LeadTagSource = EnumValues<typeof leadTagSource>;
export const LeadTagSource = {
  MANUAL: 'manual',
  UTM_AUTO: 'utm_auto',
  WEBHOOK: 'webhook',
} as const;

// ============================================
// Video Calls
// ============================================

export type CallRoomType = EnumValues<typeof callRoomType>;
export const CallRoomType = {
  CALL: 'CALL',
  STAGE: 'STAGE',
} as const;

export type CallRoomStatus = EnumValues<typeof callRoomStatus>;
export const CallRoomStatus = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  ENDED: 'ENDED',
} as const;

export type CallParticipantRole = EnumValues<typeof callParticipantRole>;
export const CallParticipantRole = {
  HOST: 'HOST',
  SPEAKER: 'SPEAKER',
  VIEWER: 'VIEWER',
} as const;

export type CallInvitationStatus = EnumValues<typeof callInvitationStatus>;
export const CallInvitationStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
} as const;

// ============================================
// Breakout Rooms (сессионные залы трансляции)
// ============================================

export type BreakoutSessionStatus = EnumValues<typeof breakoutSessionStatus>;
export const BreakoutSessionStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
} as const;

export type BreakoutSessionMode = EnumValues<typeof breakoutSessionMode>;
export const BreakoutSessionMode = {
  AUTO: 'AUTO',
  MANUAL: 'MANUAL',
} as const;

// ============================================
// Promo Codes
// ============================================

export type PromoDiscountType = EnumValues<typeof promoDiscountType>;
export const PromoDiscountType = {
  PERCENT: 'PERCENT',
  FIXED: 'FIXED',
  PRICE_OVERRIDE: 'PRICE_OVERRIDE',
} as const;

export type PromoTargetType = EnumValues<typeof promoTargetType>;
export const PromoTargetType = {
  ALL: 'ALL',
  COURSE: 'COURSE',
  CHANNEL_PASS: 'CHANNEL_PASS',
  SUBSCRIPTION_PLAN: 'SUBSCRIPTION_PLAN',
  BOOK: 'BOOK',
  PRICING_TIER: 'PRICING_TIER',
} as const;

// ============================================
// Account Link Channels
// ============================================
// Каналы привязки аккаунта, которые тег может требовать от пользователя.
// Хранится как text[] в колонке tag.requiredChannels (не pgEnum) —
// расширяется без миграций. Значения: EMAIL, TELEGRAM, GOOGLE.
// EMAIL — почта верифицирована; TELEGRAM — привязан telegramId;
// GOOGLE — OAuth-аккаунт Google.

export const AccountLinkChannel = {
  EMAIL: 'EMAIL',
  TELEGRAM: 'TELEGRAM',
  GOOGLE: 'GOOGLE',
} as const;

export type AccountLinkChannel = (typeof AccountLinkChannel)[keyof typeof AccountLinkChannel];

// ============================================
// User Invitations
// ============================================

export type InvitationStatus = EnumValues<typeof invitationStatus>;
export const InvitationStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  ACCEPTED: 'ACCEPTED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
} as const;

export type InvitationChannel = EnumValues<typeof invitationChannel>;
export const InvitationChannel = {
  TELEGRAM: 'TELEGRAM',
  EMAIL: 'EMAIL',
} as const;

// ============================================
// Livestream Restream (multistream на внешние площадки)
// ============================================
// Площадки рестрима трансляции по RTMP(S). Хранятся внутри jsonb-колонки
// Livestream.externalRestreamTargets (не pgEnum) — расширяется без миграций,
// по тому же принципу, что и AccountLinkChannel. Значения площадок:
//   YOUTUBE   — YouTube Live (RTMPS rtmps://a.rtmps.youtube.com/live2)
//   FACEBOOK  — Facebook Live (RTMPS обязателен, rtmps://live-api-s.facebook.com:443/rtmp/)
//   VK        — VK Видео Live (rtmps://ovsu.mycdn.me/input/ или RTMP legacy)
//   OK        — Одноклассники (rtmp://vsu.mycdn.me/input/, RTMPS не подтверждён)
//   CUSTOM    — произвольный RTMP(S)-приёмник (ручной URL)

export const RestreamPlatform = {
  YOUTUBE: 'YOUTUBE',
  FACEBOOK: 'FACEBOOK',
  VK: 'VK',
  OK: 'OK',
  CUSTOM: 'CUSTOM',
} as const;

export type RestreamPlatform = (typeof RestreamPlatform)[keyof typeof RestreamPlatform];

// Статус relay-процесса для конкретной площадки (runtime, не персистится в БД,
// транслируется в admin UI через WebSocket livestream.relay.status).
//   IDLE       — relay ещё не запущен (эфир не идёт / target выключен)
//   CONNECTING — ffmpeg стартовал, соединение с площадкой устанавливается
//   LIVE       — поток уходит на площадку
//   ERROR      — последняя попытка завершилась ошибкой (идёт backoff-ретрай)
//   STOPPED    — relay штатно остановлен (эфир закончился / target выключили)

export const RestreamTargetStatus = {
  IDLE: 'IDLE',
  CONNECTING: 'CONNECTING',
  LIVE: 'LIVE',
  ERROR: 'ERROR',
  STOPPED: 'STOPPED',
} as const;

export type RestreamTargetStatus =
  (typeof RestreamTargetStatus)[keyof typeof RestreamTargetStatus];

// ============================================
// MCP-сервер (docs/mcp-server-plan.md)
// ============================================

/** Вид токена MCP: персональный (из админки) или выданный по OAuth. */
export type McpTokenKind = EnumValues<typeof mcpTokenKind>;
export const McpTokenKind = {
  PERSONAL: 'PERSONAL',
  OAUTH_ACCESS: 'OAUTH_ACCESS',
  OAUTH_REFRESH: 'OAUTH_REFRESH',
} as const;

/** Scope токена MCP: чтение (GET) или чтение + запись. */
export type McpScope = (typeof McpScope)[keyof typeof McpScope];
export const McpScope = {
  READ: 'mcp:read',
  WRITE: 'mcp:write',
} as const;
